/**
 * articleContent.ts — presentation-layer helpers for the single blog
 * article page. Pure, DOM-based utilities (client-side only) that never
 * touch the network or CMS data shape:
 *
 *   - `extractHeadings`   — walks a WordPress `content.rendered` HTML
 *     string, assigns stable slug `id`s to every `<h2>`/`<h3>`, and
 *     returns both the id-injected HTML and a flat heading list for the
 *     Table of Contents.
 *   - `splitHtmlAt`       — splits an HTML string into a "before" and
 *     "after" fragment at a given ratio of top-level elements, used to
 *     insert the single inline CTA roughly 40% into the article body
 *     without altering WordPress's own content structure.
 *   - `estimateReadingTime` — word-count based reading time estimate,
 *     shared by the article hero and the related-article cards so the
 *     number is computed identically everywhere.
 *
 * None of these functions change what the CMS stores or how it is
 * fetched — they only reshape the already-fetched HTML string before it
 * is handed to `<RichContent>` for sanitization and render.
 */

export interface ExtractedHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface ExtractHeadingsResult {
  html: string;
  headings: ExtractedHeading[];
}

/**
 * Slugify heading text into a URL-safe id, disambiguating repeats
 * ("overview", "overview-1", "overview-2", ...) via the shared `seen` map.
 */
function slugifyHeading(text: string, seen: Map<string, number>): string {
  const base =
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60) || 'section';
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  return count === 0 ? base : `${base}-${count}`;
}

/**
 * Assign stable `id` attributes to every `<h2>`/`<h3>` in `html` and
 * return the mutated HTML alongside a flat list describing each heading.
 *
 * Runs entirely client-side via `DOMParser`; on the server (or in any
 * environment without `window`) it returns the input unchanged with an
 * empty heading list rather than throwing, so this is safe to call
 * unconditionally from a page component.
 */
export function extractHeadings(html: string): ExtractHeadingsResult {
  if (typeof window === 'undefined' || html.trim().length === 0) {
    return { html, headings: [] };
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nodes = Array.from(doc.querySelectorAll('h2, h3'));
  const seen = new Map<string, number>();
  const headings: ExtractedHeading[] = [];

  for (const node of nodes) {
    const text = node.textContent?.trim() ?? '';
    if (text.length === 0) continue;
    const id = slugifyHeading(text, seen);
    node.setAttribute('id', id);
    headings.push({ id, text, level: node.tagName === 'H2' ? 2 : 3 });
  }

  return { html: doc.body.innerHTML, headings };
}

/**
 * Split an HTML string into a "before" and "after" fragment at
 * approximately `ratio` of its top-level elements (default 40%).
 *
 * Used to insert a single inline CTA into the middle of the article
 * without parsing or rewriting WordPress's own markup semantics — the
 * split happens at element boundaries only, never inside a paragraph,
 * list, or heading.
 *
 * Returns `{ before: html, after: '' }` unchanged when the content is
 * too short (fewer than 4 top-level elements) to split meaningfully,
 * so short articles never show a CTA sitting awkwardly close to the top
 * or bottom.
 */
export function splitHtmlAt(
  html: string,
  ratio = 0.4,
): { before: string; after: string } {
  if (typeof window === 'undefined' || html.trim().length === 0) {
    return { before: html, after: '' };
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const children = Array.from(doc.body.children);
  if (children.length < 4) {
    return { before: html, after: '' };
  }

  const splitIndex = Math.max(
    1,
    Math.min(children.length - 1, Math.round(children.length * ratio)),
  );

  const beforeContainer = document.createElement('div');
  const afterContainer = document.createElement('div');
  children.forEach((child, i) => {
    const target = i < splitIndex ? beforeContainer : afterContainer;
    target.appendChild(child.cloneNode(true));
  });

  return { before: beforeContainer.innerHTML, after: afterContainer.innerHTML };
}

/**
 * Estimate reading time from an HTML string using a 200-words-per-minute
 * baseline (industry-standard default), rounded to the nearest minute
 * with a 1-minute floor so very short posts never show "0 min read".
 */
export function estimateReadingTime(html: string): string {
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

/**
 * Format an ISO 8601 / WP date string as "Month D, YYYY". Falls back to
 * the raw string if parsing fails so the UI never renders "Invalid Date".
 */
export function formatArticleDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Decode HTML entities in a plain-text string that contains no markup —
 * used for fields like a WordPress user's bio (`Author.description`),
 * which the REST API returns already entity-encoded (e.g. `AWS &amp;
 * GCP` for `AWS & GCP`) but with no actual HTML tags to strip. Rendering
 * such a string directly in JSX shows the literal `&amp;` text instead
 * of `&`, since JSX does not decode entities on its own.
 *
 * Uses a `<textarea>` element's `.value` round-trip (the standard
 * browser-native entity-decode trick) rather than `DOMParser`, since the
 * input is expected to be plain text — parsing it as HTML would risk
 * misinterpreting stray `<`/`>` characters as tags.
 */
export function decodeHtmlEntities(text: string): string {
  if (typeof window === 'undefined' || text.length === 0) return text;
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

/**
 * Strip HTML tags and collapse whitespace — used for the hero summary
 * (derived from `post.excerpt.rendered`), which must render as plain
 * text rather than through `<RichContent>`.
 *
 * Uses `DOMParser` (when available) so HTML entities are correctly
 * decoded — a plain tag-stripping regex leaves entity references like
 * `&hellip;` as literal text instead of the `…` character they encode.
 * WordPress's default core excerpt also appends a literal `[&hellip;]`
 * "read more" marker when a post has no custom excerpt and gets
 * auto-truncated; that marker is redundant here (the card/hero already
 * has its own "Read Article" CTA), so it is trimmed off the end.
 */
export function stripHtmlToText(html: string): string {
  let text: string;

  if (typeof window === 'undefined') {
    // Non-browser fallback: decode the one entity WP always emits by
    // default, then strip tags. Good enough for SSR/tests; the browser
    // path below is the one that actually runs in the app.
    text = html.replace(/<[^>]+>/g, ' ').replace(/&hellip;/g, '…');
  } else {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    text = doc.body.textContent ?? '';
  }

  text = text.replace(/\s+/g, ' ').trim();
  // Drop a trailing WordPress "[…]" auto-excerpt marker (with or without
  // brackets) so the summary never ends in a duplicate/redundant ellipsis.
  text = text.replace(/\s*\[?…\]?\s*$/, '').trim();
  return text;
}
